<template>
    <v-layout row>      
        <v-flex xs12 sm12>         
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    
                    <!-- Back -->
                    <v-btn dark icon title="Back to My Teams" @click="$router.push('/myteam')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>

                    <!-- View This Month -->
                    <v-btn dark icon title="View this month" @click="viewThisMonth()">
                        <v-icon>far fa-calendar-alt</v-icon>
                    </v-btn>

                    <v-spacer></v-spacer>

                    <!-- Prev -->
                    <v-btn dark icon small title="Previous Month" @click="changeMonth(-1)" :disabled="loading" class="mr-0">
                        <v-icon>chevron_left</v-icon>
                    </v-btn>

                    <!-- Month -->
                    <v-toolbar-title class="subheading transparent white--text">
                        {{ startMonth | moment("MMM YYYY") }}
                    </v-toolbar-title>

                    <!-- Next -->
                    <v-btn dark icon small title="Next Month" @click="changeMonth(1)" :disabled="loading">
                        <v-icon>chevron_right</v-icon>
                    </v-btn>
                </v-toolbar>
            </v-card>
            
            <!-- Error -->
            <v-alert v-if="error" :value="true" color="warning" icon="warning" outline class="mt-8">
                {{ errorMessage }}
            </v-alert>  

            <v-layout row wrap class="mt-5 mb-5"> 
                <v-flex xs12>
                    <v-card class="elevation-1 transparent"> 
                        <v-sheet height="500">

                            <v-calendar ref="calendar" v-model="startMonth" :now="today" color="primary">
                                <template v-slot:day="{ date }">
                                    <div v-for="(event, index) in eventsMap[date]" :key="date + '-' + index">

                                        <!-- Holiday -->
                                        <v-menu v-model="event.menuHoliday" full-width offset-x>
                                            <template v-slot:activator="{ on }">
                                                <v-chip
                                                    v-if="event.holiday"
                                                    v-on="on"
                                                    small
                                                    color="#BBDEFB"
                                                >
                                                    {{ event.holiday }}
                                                </v-chip>
                                            </template> 

                                            <v-card max-width="400px" flat>
                                                <v-toolbar flat height="45px">
                                                    <b>{{ event.holiday }}</b>
                                                    <v-spacer></v-spacer>
                                                </v-toolbar>

                                                <v-card-text>
                                                    {{ event.holidayType }}
                                                </v-card-text>
                                            </v-card> 
                                        </v-menu>

                                        <!-- Birthday -->
                                        <v-menu v-model="event.menuBirthday" full-width offset-x>
                                            <template v-slot:activator="{ on }">
                                                <v-chip
                                                    v-if="event.withBirthDay"
                                                    v-on="on"
                                                    small
                                                    color="#F8BBD0"
                                                >
                                                    Birthdays
                                                </v-chip>
                                            </template> 

                                            <v-card max-width="400px" flat>
                                                <v-toolbar flat height="45px">
                                                    <b>Happy Birthday</b>
                                                    <v-spacer></v-spacer>
                                                </v-toolbar>

                                                <v-card-text>
                                                    <div v-for="(item, i) in event.birthdayItems" :key="i">
                                                        <v-chip small>
                                                            <v-avatar size="20">
                                                                <v-img :src="awsS3Helper.getAvatarUrl(item.userId)" />
                                                            </v-avatar>
                                                            {{ item.firstName }} {{ item.lastName }}
                                                        </v-chip>
                                                        <br>
                                                    </div>
                                                </v-card-text>
                                            </v-card> 
                                        </v-menu>

                                        <!-- Time Off -->
                                        <v-chip v-if="event.withPTO" @click="showPTOPopup(event)" small color="#C8E6C9">
                                            <span v-if="isMobile">PTO</span>
                                            <span v-else>Time Offs</span>
                                        </v-chip>

                                        <!-- Attendance Corrections -->
                                        <v-chip v-if="event.withAttendanceCorrections" @click="showAttendanceCorrectionsPopup(event)" small color="#FFE0B2">
                                            Corrections
                                        </v-chip>

                                        <!-- Overtime -->
                                        <v-chip v-if="event.withOvertime" @click="showOvertimePopup(event)" small color="#D1C4E9">
                                            <span v-if="isMobile">OT</span>
                                            <span v-else>Overtime</span>
                                        </v-chip>
                                    </div>
                                </template>
                            </v-calendar>
                        </v-sheet>
                    </v-card>
                </v-flex>
            </v-layout>
        </v-flex>

        <!-- popups -->
        <v-dialog v-model="dialogPTOList" width="800">
            <pto-list ref="refPTOList" :dialogPTOList.sync="dialogPTOList"></pto-list>
        </v-dialog>
        <v-dialog v-model="dialogOvertimeList" width="800">
            <ot-list ref="refOvertimeList" :dialogOvertimeList.sync="dialogOvertimeList"></ot-list>
        </v-dialog>
        <v-dialog v-model="dialogAttendanceCorrectionsList" width="800">
            <corrections-list ref="refCorrectionsList" :dialogAttendanceCorrectionsList.sync="dialogAttendanceCorrectionsList"></corrections-list>
        </v-dialog>
    </v-layout>
</template>

<script>

import { syncTeamContext } from '@/helpers/team-selector'
import teamApi from '@/api/team'
import awsS3Helper from '@/helpers/aws-s3'

export default {
    name: 'TeamCalendar',
    metaInfo: {
        title: 'Team Calendar',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: "Team Calendar" },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    }, 
    components: {
        'pto-list': () => import('@/components/TimeOff/TimeOffsByTeamAndDate'),
        'ot-list': () => import('@/components/Overtime/OvertimeByTeamAndDate'),
        'corrections-list': () => import('@/components/AttendanceCorrections/AttendanceCorrectionsByTeamAndDate'),
    },   
    data() {
        return {
            awsS3Helper,
            loading: false,
            error: false,
            errorMessage: '',
            currentDate: Date.now(), // single source of truth
            events: [],
            dialogPTOList: false,
            dialogOvertimeList: false,
            dialogAttendanceCorrectionsList: false
        }
    },
    methods: {
        showAttendanceCorrectionsPopup(data) {
            const formatted = this.$moment(data.transDate).format('YYYY-MM-DD')
            this.$refs.refCorrectionsList.getAttendanceCorrections(formatted)
            this.dialogAttendanceCorrectionsList = true
        },
        showOvertimePopup(data) {
            const formatted = this.$moment(data.transDate).format('YYYY-MM-DD')
            this.$refs.refOvertimeList.getOvertime(formatted)
            this.dialogOvertimeList = true
        },
        showPTOPopup(data) {
            const formatted = this.$moment(data.transDate).format('YYYY-MM-DD')
            this.$refs.refPTOList.initialize(formatted)
            this.dialogPTOList = true
        },
        formatDate(date) {
            return this.$moment(date).format("YYYY-MM-DD")
        },
        async fetchEvents(date) {
            try {
                this.loading = true
                const response = await teamApi.getTeamEventsByMonth(this.formatDate(date))
                if (response && response.status === 200) {
                    this.events = response.data.items
                }
            } catch (error) {
                this.error = true
                this.errorMessage = error
            } finally {
                this.loading = false
            }
        },
        async changeMonth(offset) {
            this.currentDate = this.$moment(this.currentDate)
                .add(offset, 'month')
                .valueOf()

            await this.fetchEvents(this.currentDate)
        },
        async viewThisMonth() {
            this.currentDate = Date.now()
            await this.fetchEvents(this.currentDate)
        },
        viewDaily(transDate) {
            this.$router.push('/myattendance/' + this.formatDate(transDate))
        },
        teamChangeHandler(data) {
            if (data === 'team-changed-handler') {
                this.fetchEvents(this.currentDate)
            }
        }
    },
    async mounted() {
        await syncTeamContext()
    },
    async created() {
        if (this.$store.state.selectedTeamId) {
            this.fetchEvents(this.currentDate)
        }
        
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Team Calendar',
            isTeamPage: true
        })

        // Listen for the change event
        this.$root.$on('team-changed', this.teamChangeHandler)
    },
    beforeDestroy() {
        this.$root.$off('team-changed', this.teamChangeHandler)
    },
    computed: {
        today() {
            return this.formatDate(this.currentDate)
        },
        startMonth() {
            return this.formatDate(this.currentDate)
        },
        eventsMap() {
            return this.events.reduce((map, e) => {
                const key = this.formatDate(e.transDate)
                if (!map[key]) map[key] = []
                map[key].push(e)
                return map
            }, {})
        }
    },
}
</script>